import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { supabase } from "@/integrations/supabase/client";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { OrgProvider } from "@/contexts/OrgContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AdminRouteGuard } from "@/guards/AdminRouteGuard";
import { DashboardRouteGuard } from "@/guards/DashboardRouteGuard";
import { PortalRouteGuard } from "@/guards/PortalRouteGuard";
import AdminShell from "./pages/AdminShell";
import AdminLogin from "./pages/AdminLogin";
import PortalLogin from "./pages/PortalLogin";
import PortalRegister from "./pages/PortalRegister";
import PortalShell from "./pages/PortalShell";
import Landing from "./pages/Landing";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Enterprise from "./pages/Enterprise";
import Pricing from "./pages/Pricing";
import Platform from "./pages/Platform";
import SocialSuitePage from "./pages/SocialSuitePage";
import CoveragePage from "./pages/CoveragePage";
import JournalistsPage from "./pages/JournalistsPage";
import FaqPage from "./pages/FaqPage";
import Setup from "./pages/Setup";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import PublicProfile from "./pages/PublicProfile";
import PublicBioPage from "./pages/PublicBioPage";
import AccreditationCheck from "./pages/AccreditationCheck";
import NotFound from "./pages/NotFound";
import OAuthCallback from "./pages/OAuthCallback";
import Profile from "./pages/Profile";
import Billing from "./pages/Billing";
import Support from "./pages/Support";
import Settings from "./pages/Settings";
import Language from "./pages/Language";

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    // Route guards (DashboardRouteGuard, AdminRouteGuard, PortalRouteGuard)
    // cache their "is this user staff/admin/journalist" RPC result under a
    // fixed queryKey. React Query serves that cached value immediately on
    // first render (even if stale) while it refetches in the background —
    // so signing in right after an anonymous visit reused the old cached
    // `false`, causing the guard to redirect away and only self-correct
    // once the background refetch landed. That bounce is what looked like
    // a flickering dashboard. Clearing the cache on every sign-in/out
    // forces a fresh check instead of trusting a pre-login cache entry.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        queryClient.clear();
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  return (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <OrgProvider>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            <Route path="/enterprise" element={<Enterprise />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/platform" element={<Platform />} />
            <Route path="/social-suite" element={<SocialSuitePage />} />
            <Route path="/coverage" element={<CoveragePage />} />
            <Route path="/journalists" element={<JournalistsPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/setup" element={<Setup />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/press/:slug" element={<PublicProfile />} />
            <Route path="/bio/:slug" element={<PublicBioPage />} />
            <Route path="/:orgSlug/accreditation-check" element={<AccreditationCheck />} />
            <Route path="/verify/:orgSlug" element={<AccreditationCheck />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardRouteGuard>
                  <Index />
                </DashboardRouteGuard>
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/billing" element={
              <ProtectedRoute>
                <Billing />
              </ProtectedRoute>
            } />
            <Route path="/support" element={
              <ProtectedRoute>
                <Support />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } />
            <Route path="/language" element={
              <ProtectedRoute>
                <Language />
              </ProtectedRoute>
            } />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/*" element={
              <AdminRouteGuard>
                <AdminShell />
              </AdminRouteGuard>
            } />
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/register" element={<PortalRegister />} />
            <Route path="/portal/*" element={
              <PortalRouteGuard>
                <PortalShell />
              </PortalRouteGuard>
            } />
            {/* White-label: slug-based branded portal URLs */}
            <Route path="/orgs/:slug/login" element={<PortalLogin />} />
            <Route path="/orgs/:slug/portal/*" element={
              <PortalRouteGuard>
                <PortalShell />
              </PortalRouteGuard>
            } />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
    </OrgProvider>
  </QueryClientProvider>
  </ErrorBoundary>
  );
};

export default App;
